import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, FileUp, ImageUp, Sparkles, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import type { Block, PostSummary } from "@/lib/site";
import { SECTIONS } from "@/lib/site";
import { ContentPlanBar, PostImageButton } from "@/components/ContentPlanBar";
import { CraftImagesBar } from "@/components/CraftImagesBar";
import { PhotoWarning, PinPreview } from "@/components/PinPreview";
import { PageHeader } from "@/components/Doodles";
import { PostBlocks } from "@/components/site/PostBlocks";

function UploadButton({ label, accept, url, icon: Icon, onDone, testId }: { label: string; accept: string; url: string; icon: any; onDone: () => void; testId: string }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(url, form, { timeout: 600000 });
      toast.success(data.found != null ? `Imported ${data.found} posts (${data.created} new, ${data.updated} updated)` : `Saved ${data.saved} images`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testId}-input`} />
      <button className="btn-outline" disabled={busy} onClick={() => ref.current?.click()} data-testid={testId}><Icon className="h-4 w-4" strokeWidth={2.5} />{busy ? "Uploading…" : label}</button>
    </>
  );
}

function DraftPanel({ slug, isNew, hasPhoto, onClose, onPhoto }: { slug: string; isNew: boolean; hasPhoto: boolean; onClose: () => void; onPhoto: () => void }) {
  const [blocks, setBlocks] = useState<Block[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [tries, setTries] = useState(0);
  const [photoVersion, setPhotoVersion] = useState(0);
  useEffect(() => {
    setFailed(false);
    setBlocks(null);
    api.get(`/admin/posts/${slug}`).then((r) => setBlocks(r.data.draft?.blocks || [])).catch((e) => { toast.error(formatApiError(e), { id: `draft-${slug}` }); setFailed(true); });
  }, [slug, tries]);
  const act = async (approve: boolean) => {
    if (approve && isNew && !hasPhoto && !window.confirm("This post has no photo yet. Publish it with the section's stock photo?")) return;
    try {
      if (approve) await api.post(`/admin/posts/${slug}/draft/approve`); else await api.delete(`/admin/posts/${slug}/draft`);
      toast.success(approve ? (isNew ? "Post published" : "Published the refreshed post") : "Draft discarded");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="mt-4 rounded-[22px] border-2 border-dashed border-[#23324A] bg-[#FDFBF7] p-5" data-testid={`draft-panel-${slug}`}>
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#F08A6C]">AI draft. Read it before publishing.</p>
      {!hasPhoto && <PhotoWarning slug={slug} onDone={() => { setPhotoVersion((v) => v + 1); onPhoto(); }} />}
      {failed ? (
        <p className="mt-3 text-sm font-semibold text-[#23324A]" data-testid="draft-load-error">Couldn't load this draft. <button className="underline" onClick={() => setTries((t) => t + 1)} data-testid="draft-retry-btn">Try again</button></p>
      ) : blocks ? <PostBlocks blocks={blocks} /> : <p className="text-sm">Loading…</p>}
      <PinPreview slug={slug} version={photoVersion} />
      <div className="mt-6 flex gap-3">
        <button className="btn-coral" disabled={!blocks?.length} onClick={() => act(true)} data-testid="approve-draft-btn"><Check className="h-4 w-4" strokeWidth={3} />{isNew ? "Publish post" : "Publish refresh"}</button>
        <button className="btn-outline" onClick={() => act(false)} data-testid="discard-draft-btn"><X className="h-4 w-4" strokeWidth={3} />Discard</button>
      </div>
    </div>
  );
}

export default function ContentPage() {
  const [params] = useSearchParams();
  const [posts, setPosts] = useState<PostSummary[] | null>(null);
  const [open, setOpen] = useState(params.get("open") || "");
  const [busy, setBusy] = useState("");
  const [filter, setFilter] = useState(params.get("open") ? "drafts" : "all");
  const [picked, setPicked] = useState<string[]>([]);
  const load = useCallback(() => { api.get("/admin/posts").then((r) => setPosts(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  const deepLink = params.get("open");
  useEffect(() => {
    if (!posts || !deepLink) { return; }
    const t = setTimeout(() => document.querySelector(`[data-testid='draft-panel-${deepLink}']`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    return () => clearTimeout(t);
  }, [posts, deepLink]);

  const refresh = async (slug: string) => {
    setBusy(slug);
    try { await api.post(`/admin/posts/${slug}/refresh`, null, { timeout: 180000 }); setOpen(slug); load(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const setSection = async (p: PostSummary, section: string) => {
    try { await api.patch(`/admin/posts/${p.slug}`, { section }); toast.success("Section saved"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const shown = (posts || []).filter((p) => filter === "all" || (filter === "drafts" ? p.has_draft : filter === "planned" ? p.status === "planned" : p.section === (filter === "none" ? "" : filter)));
  const count = (s: string) => (posts || []).filter((p) => p.section === s).length;
  const shownDrafts = shown.filter((p) => p.has_draft).map((p) => p.slug);
  const selected = picked.filter((s) => shownDrafts.includes(s));
  const togglePick = (slug: string) => setPicked((cur) => (cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug]));
  const publishPicked = async () => {
    const noPhoto = (posts || []).filter((p) => selected.includes(p.slug) && !p.image).length;
    const warn = noPhoto ? ` ${noPhoto} of them ${noPhoto === 1 ? "has" : "have"} no photo yet and will use the section's stock photo.` : "";
    if (!window.confirm(`Publish ${selected.length} post${selected.length === 1 ? "" : "s"} now? Each new post also gets 3 Pinterest pins scheduled.${warn}`)) return;
    try {
      const { data } = await api.post("/admin/posts/drafts/approve-batch", { slugs: selected });
      toast.success(`Published ${data.published} post${data.published === 1 ? "" : "s"}`);
      setPicked([]);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="content-page">
      <PageHeader title="Articles" kicker="Import · sort into sections · refresh">
        <div className="flex flex-wrap gap-2">
          <UploadButton label="Import WordPress export (.xml)" accept=".xml" url="/admin/posts/import" icon={FileUp} onDone={load} testId="import-wxr-btn" />
          <UploadButton label="Import images (.zip)" accept=".zip" url="/admin/posts/import-media" icon={ImageUp} onDone={load} testId="import-media-btn" />
        </div>
      </PageHeader>
      <ContentPlanBar onChange={load} />
      <CraftImagesBar onChange={load} />
      <div className="mb-5 flex flex-wrap gap-2" data-testid="content-filters">
        <button className={`chip ${filter === "all" ? "chip-active" : ""}`} onClick={() => setFilter("all")} data-testid="content-filter-all">All ({posts?.length ?? 0})</button>
        <button className={`chip ${filter === "drafts" ? "chip-active" : ""}`} onClick={() => setFilter("drafts")} data-testid="content-filter-drafts">Drafts to approve ({(posts || []).filter((p) => p.has_draft).length})</button>
        <button className={`chip ${filter === "planned" ? "chip-active" : ""}`} onClick={() => setFilter("planned")} data-testid="content-filter-planned">Hidden ({(posts || []).filter((p) => p.status === "planned").length})</button>
        {SECTIONS.map((s) => (
          <button key={s.slug} className={`chip ${filter === s.slug ? "chip-active" : ""}`} onClick={() => setFilter(s.slug)} data-testid={`content-filter-${s.slug}`}>{s.short} ({count(s.slug)})</button>
        ))}
        <button className={`chip ${filter === "none" ? "chip-active" : ""}`} onClick={() => setFilter("none")} data-testid="content-filter-none">Needs a section ({count("")})</button>
      </div>
      {shownDrafts.length > 0 && (
        <div className="sticky top-2 z-20 mb-4 flex flex-wrap items-center gap-3 rounded-2xl border-2 border-[#23324A] bg-[#F6D776] px-4 py-3" data-testid="batch-bar">
          <label className="flex items-center gap-2 text-sm font-bold text-[#23324A]">
            <input type="checkbox" className="crayon-check" checked={shownDrafts.every((s) => picked.includes(s))}
              onChange={(e) => setPicked(e.target.checked ? Array.from(new Set([...picked, ...shownDrafts])) : picked.filter((s) => !shownDrafts.includes(s)))} data-testid="batch-select-all" />
            Select all {shownDrafts.length} drafts shown
          </label>
          <span className="flex-1 text-sm text-[#23324A]/75" data-testid="batch-count">{selected.length} selected</span>
          <button className="btn-navy" disabled={!selected.length || selected.length > 100} onClick={publishPicked} data-testid="batch-publish-btn">Publish selected</button>
        </div>
      )}
      <ul className="space-y-3" data-testid="content-list">
        {shown.map((p) => (
          <li key={p.slug} className="rounded-2xl border-2 border-[#23324A]/20 bg-white p-4" data-testid={`content-row-${p.slug}`}>
            <div className="flex flex-wrap items-center gap-3">
              {p.has_draft && (
                <input type="checkbox" className="crayon-check" checked={picked.includes(p.slug)} onChange={() => togglePick(p.slug)} aria-label={`Select ${p.title}`} data-testid={`batch-check-${p.slug}`} />
              )}
              {p.status === "planned" ? (
                <span className="min-w-0 flex-1 font-semibold text-[#23324A]">{p.title}</span>
              ) : (
                <a href={`/${p.slug}`} target="_blank" rel="noreferrer" className="min-w-0 flex-1 font-semibold text-[#23324A] hover:underline">{p.title}</a>
              )}
              <span className="text-xs font-bold text-[#23324A]/60">{p.status === "planned" ? "Hidden" : p.refreshed ? "Step-by-step" : "Original"}{p.has_draft ? " · draft ready" : ""}</span>
              {!p.image && <span className="rounded-full bg-[#FDE6DF] px-2 py-0.5 text-xs font-bold text-[#23324A]" data-testid={`needs-photo-${p.slug}`}>Needs photo</span>}
              <PostImageButton slug={p.slug} onDone={load} />
              <select value={p.section} onChange={(e) => setSection(p, e.target.value)} aria-label="Section"
                className={`rounded-full border-2 px-3 py-1.5 text-sm font-bold text-[#23324A] ${p.section ? "border-[#23324A]/25 bg-white" : "border-[#F08A6C] bg-[#FDE6DF]"}`} data-testid={`section-select-${p.slug}`}>
                <option value="">Needs a section</option>
                {SECTIONS.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
              </select>
              {p.has_draft ? (
                <button className="btn-navy" onClick={() => setOpen(open === p.slug ? "" : p.slug)} data-testid={`view-draft-${p.slug}`}>Review draft</button>
              ) : (
                <button className="btn-outline" disabled={!!busy} onClick={() => refresh(p.slug)} data-testid={`refresh-${p.slug}`}>
                  <Sparkles className="h-4 w-4" strokeWidth={2.5} />{busy === p.slug ? "Writing… (~30s)" : "Refresh"}
                </button>
              )}
            </div>
            {open === p.slug && p.has_draft && <DraftPanel slug={p.slug} isNew={p.status === "planned"} hasPhoto={!!p.image} onPhoto={load} onClose={() => { setOpen(""); load(); }} />}
          </li>
        ))}
      </ul>
    </div>
  );
}
